import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

// ─── Field Execution Journal (formType 1) bulk import ────────────────────────
// One row per sub-work. Unit / Main Work cells may be left blank to mean
// "same as the row above" (fill-down), so a villa with many works reads
// cleanly. A row with a Main Work but no Sub Work still creates the main work.

const COLUMNS = [
  { key: "unitName", header: "Unit Name", width: 22 },
  { key: "mainWorkName", header: "Main Work", width: 24 },
  { key: "mainStartDate", header: "Main Start Date", width: 18 },
  { key: "mainCompletionDate", header: "Main Completion Date", width: 20 },
  { key: "mainStatus", header: "Main Status", width: 16 },
  { key: "subWorkName", header: "Sub Work", width: 24 },
  { key: "subStartDate", header: "Sub Start Date", width: 18 },
  { key: "subCompletionDate", header: "Sub Completion Date", width: 20 },
  { key: "subStatus", header: "Sub Status", width: 16 },
  { key: "subRemarks", header: "Sub Remarks", width: 40 },
];

// header text (any case / spacing / punctuation) → column key
const HEADER_ALIASES = {
  "unit name": "unitName",
  unit: "unitName",
  "main work": "mainWorkName",
  "main work name": "mainWorkName",
  "main start date": "mainStartDate",
  "main work start date": "mainStartDate",
  "main completion date": "mainCompletionDate",
  "main work completion date": "mainCompletionDate",
  "main status": "mainStatus",
  "main work status": "mainStatus",
  "sub work": "subWorkName",
  "sub work name": "subWorkName",
  subwork: "subWorkName",
  "sub start date": "subStartDate",
  "sub work start date": "subStartDate",
  "sub completion date": "subCompletionDate",
  "sub work completion date": "subCompletionDate",
  "sub status": "subStatus",
  "sub work status": "subStatus",
  "sub remarks": "subRemarks",
  remarks: "subRemarks",
  "sub work remarks": "subRemarks",
};

const normHeader = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");

const normKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

// ExcelJS cell values come through as strings, numbers, JS Dates, or rich
// objects — flatten to a primitive the backend can coerce.
const readCell = (cell) => {
  if (!cell) return "";
  const v = cell.value;
  if (v === null || v === undefined) return "";
  if (v instanceof Date) return v;
  if (typeof v === "object") {
    if (Array.isArray(v.richText))
      return v.richText.map((part) => part.text).join("");
    if (v.text !== undefined) return v.text;
    if (v.result !== undefined) return v.result;
    if (v.hyperlink !== undefined) return v.hyperlink;
    return "";
  }
  return v;
};

const isoDate = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).trim();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// ── Template ────────────────────────────────────────────────────────────────
export const downloadFieldImportTemplate = async () => {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Import");

  sheet.columns = COLUMNS.map((col) => ({
    header: col.header,
    key: col.key,
    width: col.width,
  }));
  sheet.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
  sheet.getRow(1).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF1F2937" },
  };
  sheet.getRow(1).alignment = { vertical: "middle" };

  const sample = [
    ["Villa 21", "Electrical", "2026-05-01", "2026-06-30", "In Progress", "Internal Wiring", "2026-05-01", "2026-05-20", "In Progress", "1st floor pending"],
    ["", "", "", "", "", "Lighting", "2026-05-10", "2026-06-05", "Pending", ""],
    ["", "Plumbing", "2026-05-05", "2026-07-10", "Pending", "Supply Lines", "2026-05-05", "2026-06-01", "Pending", ""],
    ["Villa 22", "Electrical", "2026-05-01", "2026-06-30", "Pending", "Internal Wiring", "2026-05-01", "2026-05-20", "Pending", ""],
    ["", "", "", "", "", "Lighting", "2026-05-10", "2026-06-05", "Pending", ""],
  ];
  sample.forEach((row) => sheet.addRow(row));

  const notes = workbook.addWorksheet("Instructions");
  notes.getColumn(1).width = 110;
  [
    "FIELD EXECUTION JOURNAL — BULK IMPORT",
    "",
    "• One row per SUB WORK.",
    "• Leave 'Unit Name' / 'Main Work' blank to reuse the value from the row above (fill-down).",
    "• A row with a 'Main Work' but no 'Sub Work' still creates that main work (with no sub works).",
    "• 'Main Start/Completion Date' and 'Main Status' are read from the FIRST row of each main work.",
    "• Dates: any recognisable date (2026-05-01, 01/05/2026, or an Excel date cell).",
    "• Status: Pending / In Progress / Complete (anything else maps to the closest match).",
    "• Each unit becomes one reporting sheet. If a sheet already exists for that unit, the",
    "  imported main works are merged into it.",
  ].forEach((line) => notes.addRow([line]));

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    "field-sheet-bulk-import-template.xlsx",
  );
};

// ── Parse an uploaded workbook into { units, warnings } ──────────────────────
export const parseFieldImportWorkbook = async (file) => {
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());
  const sheet = workbook.worksheets[0];
  if (!sheet) {
    return { units: [], warnings: ["The file has no worksheets."], rowCount: 0 };
  }

  // Header row → column index map
  const headerRow = sheet.getRow(1);
  const colOf = {};
  headerRow.eachCell((cell, colNumber) => {
    const key = HEADER_ALIASES[normHeader(readCell(cell))];
    if (key && colOf[key] === undefined) colOf[key] = colNumber;
  });

  const missing = ["unitName", "mainWorkName"].filter(
    (k) => colOf[k] === undefined,
  );
  if (missing.length) {
    return {
      units: [],
      warnings: [
        `Missing required column(s): ${missing
          .map((k) => COLUMNS.find((c) => c.key === k).header)
          .join(", ")}. Use the template.`,
      ],
      rowCount: 0,
    };
  }

  const get = (row, key) =>
    colOf[key] === undefined ? "" : readCell(row.getCell(colOf[key]));

  const unitsMap = new Map();
  const warnings = [];
  let lastUnit = "";
  let lastMain = "";
  let rowCount = 0;

  sheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return;

    let unitName = String(get(row, "unitName") || "").trim();
    let mainWorkName = String(get(row, "mainWorkName") || "").trim();
    const subWorkName = String(get(row, "subWorkName") || "").trim();

    if (!unitName) unitName = lastUnit;
    // a new unit resets the "same main work as above" carry-over
    if (unitName !== lastUnit) lastMain = "";
    if (!mainWorkName) mainWorkName = lastMain;

    // fully blank row → skip silently
    if (!unitName && !mainWorkName && !subWorkName) return;

    if (!unitName) {
      warnings.push(`Row ${rowNumber}: no unit name — skipped.`);
      return;
    }
    if (!mainWorkName) {
      warnings.push(`Row ${rowNumber}: no main work — skipped.`);
      return;
    }

    lastUnit = unitName;
    lastMain = mainWorkName;
    rowCount += 1;

    const unitKey = normKey(unitName);
    if (!unitsMap.has(unitKey)) {
      unitsMap.set(unitKey, { unitName, mains: new Map() });
    }
    const unit = unitsMap.get(unitKey);

    const mainKey = normKey(mainWorkName);
    if (!unit.mains.has(mainKey)) {
      unit.mains.set(mainKey, {
        mainWorkName,
        startDate: isoDate(get(row, "mainStartDate")),
        completionDate: isoDate(get(row, "mainCompletionDate")),
        status: String(get(row, "mainStatus") || "").trim() || "Pending",
        subWorks: [],
      });
    }
    const main = unit.mains.get(mainKey);
    // fill any main-level field that the first row left blank
    if (!main.startDate) main.startDate = isoDate(get(row, "mainStartDate"));
    if (!main.completionDate)
      main.completionDate = isoDate(get(row, "mainCompletionDate"));
    if (!main.status || main.status === "Pending") {
      const s = String(get(row, "mainStatus") || "").trim();
      if (s) main.status = s;
    }

    if (subWorkName) {
      main.subWorks.push({
        subWorkName,
        startDate: isoDate(get(row, "subStartDate")),
        completionDate: isoDate(get(row, "subCompletionDate")),
        status: String(get(row, "subStatus") || "").trim() || "Pending",
        remarks: String(get(row, "subRemarks") || "").trim(),
      });
    }
  });

  const units = Array.from(unitsMap.values()).map((unit) => ({
    unitName: unit.unitName,
    mainWorks: Array.from(unit.mains.values()),
  }));

  return { units, warnings, rowCount };
};
